// assets/product-custom-property.js
import { Component } from '@theme/component';
import { formatMoney } from '@theme/money-formatting';
import { ProductSelectEvent, StandardEvents } from '@shopify/events';

const discountTiers = [
  { minQuantity: 50, percentage: 25 },
  { minQuantity: 35, percentage: 20 },
  { minQuantity: 20, percentage: 15 },
  { minQuantity: 10, percentage: 10 },
];

/**
 * @typedef {object} ProductCustomPropertyRefs
 * @property {HTMLInputElement | HTMLTextAreaElement} textInput - The text input.
 * @property {HTMLElement} characterCount - The character count element.
 */

/**
 * A custom element that manages product custom properties
 * @extends Component<ProductCustomPropertyRefs>
 */
class ProductCustomProperty extends Component {
  #abortController = new AbortController();
  #variantPrice = 0;
  #unitPriceDisplayUsesLeadingCurrency;

  connectedCallback() {
    super.connectedCallback();

    if (this.#isPricePerCharacter) {
      this.#variantPrice = Number(this.#pricePerCharacterContainer?.dataset.variantPrice) || 0;
      const target = this.closest('.shopify-section, dialog, product-card');
      target?.addEventListener(StandardEvents.productSelect, this.#onProductSelect, {
        signal: this.#abortController.signal,
      });
      this.#variantPicker?.addEventListener('change', () => {
        this.#schedulePricePerCharacterUpdate();
      }, {
        signal: this.#abortController.signal,
      });
      this.#sectionRoot?.querySelectorAll('[data-price-per-character-surface-input]').forEach((input) => {
        input.addEventListener('change', (event) => {
          event.stopPropagation();
          this.#updatePricePerCharacter();
        }, {
          signal: this.#abortController.signal,
        });
      });
      this.#sectionRoot?.querySelectorAll('[data-price-per-character-color-input]').forEach((input) => {
        input.addEventListener('change', (event) => {
          event.stopPropagation();
          this.#updatePricePerCharacter();
        }, {
          signal: this.#abortController.signal,
        });
      });
      this.#updateSurfacePreview();
      this.#resizeTextInput();
      this.#updatePricePerCharacter();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  handleInput() {
    this.#resizeTextInput();
    this.#updateCharacterCount();
    this.#updatePricePerCharacter();
  }

  #resizeTextInput() {
    if (!this.#isPricePerCharacter) return;

    const { textInput } = this.refs;
    if (!(textInput instanceof HTMLTextAreaElement)) return;

    textInput.style.height = 'auto';
    textInput.style.height = `${textInput.scrollHeight}px`;
  }

  #updateCharacterCount() {
    const { characterCount, textInput } = this.refs;
    const currentLength = this.#isPricePerCharacter
      ? this.#countBillableCharacters(textInput.value)
      : textInput.value.length;
    const maxLength = textInput.maxLength;

    const template = characterCount.getAttribute('data-template');
    if (!template) return;

    const updatedText = template.replace('[current]', currentLength.toString()).replace('[max]', maxLength.toString());

    characterCount.textContent = updatedText;
  }

  get #isPricePerCharacter() {
    return Boolean(this.#pricePerCharacterContainer);
  }

  get #pricePerCharacterContainer() {
    return /** @type {HTMLElement | null} */ (this.querySelector('[data-price-per-character-property]'));
  }

  #countBillableCharacters(value) {
    const text = value || '';

    if ('Segmenter' in Intl) {
      const segmenter = new Intl.Segmenter(undefined, { granularity: 'grapheme' });
      return Array.from(segmenter.segment(text)).filter(({ segment }) => !/^\s+$/u.test(segment)).length;
    }

    return Array.from(text).filter((character) => !/\s/u.test(character)).length;
  }

  #formatPrice(cents) {
    const container = this.#pricePerCharacterContainer;
    const currency = container?.dataset.currency || 'EUR';
    const moneyFormat =
      currency === 'EUR' ? '{{amount_with_comma_separator}} €' : container?.dataset.moneyFormat || '{{amount_with_comma_separator}} €';

    return formatMoney(cents, moneyFormat, currency);
  }

  #getSelectedOptions() {
    return Object.fromEntries(this.#getSelectedOptionEntries());
  }

  #getSelectedOptionEntries() {
    const variantPicker = this.closest('.shopify-section')?.querySelector('variant-picker');
    /** @type {Array<[string, string]>} */
    const selectedOptions = [];

    variantPicker?.querySelectorAll('.variant-option--dropdowns').forEach((option) => {
      const label = option.querySelector('label')?.textContent?.trim();
      const select = option.querySelector('select');

      if (label && select instanceof HTMLSelectElement) {
        selectedOptions.push([label, select.value]);
      }
    });

    variantPicker?.querySelectorAll('fieldset.variant-option').forEach((fieldset) => {
      const legend = fieldset.querySelector('legend');
      const label = legend?.childNodes[0]?.textContent?.trim() || legend?.textContent?.trim();
      const input = fieldset.querySelector('input:checked');

      if (label && input instanceof HTMLInputElement) {
        selectedOptions.push([label, input.value]);
      }
    });

    return selectedOptions;
  }

  #setText(selector, value) {
    const element = this.#sectionRoot?.querySelector(selector) || this.querySelector(selector);
    if (element) element.textContent = value;
  }

  #setHidden(selector, hidden) {
    const element = this.#sectionRoot?.querySelector(selector) || this.querySelector(selector);
    if (element instanceof HTMLElement) {
      element.hidden = hidden;
    }
  }

  #setInput(selector, value) {
    const input = this.querySelector(selector);
    if (input instanceof HTMLInputElement) {
      input.value = value;
    }
  }

  get #sectionRoot() {
    return this.closest('.shopify-section');
  }

  get #productForm() {
    return this.#sectionRoot?.querySelector('form[data-type="add-to-cart-form"]');
  }

  get #variantPicker() {
    return this.#sectionRoot?.querySelector('variant-picker');
  }

  #schedulePricePerCharacterUpdate() {
    window.requestAnimationFrame(() => this.#updatePricePerCharacter());
    window.setTimeout(() => this.#updatePricePerCharacter(), 80);
    window.setTimeout(() => this.#updatePricePerCharacter(), 250);
  }

  #updatePricePerCharacter() {
    if (!this.#isPricePerCharacter) return;

    const { textInput } = this.refs;
    if (!textInput) return;

    const billableCharacters = this.#countBillableCharacters(textInput.value);
    const selectedOptionEntries = this.#getSelectedOptionEntries();
    const unitPrice = this.#variantPrice;
    const totalPrice = billableCharacters * unitPrice;
    const discount = this.#getDiscount(billableCharacters, unitPrice, totalPrice);
    const discountedTotalPrice = discount.discountedTotalPrice;
    const formattedUnitPrice = this.#formatPrice(unitPrice);
    const formattedTotalPrice = this.#formatPrice(totalPrice);
    const formattedDiscountedTotalPrice = this.#formatPrice(discountedTotalPrice);
    const summaryTextValue = textInput.value.trim() || '-';
    const summaryCharacterLabel = billableCharacters === 1 ? 'Zeichen' : 'Zeichen';
    const summaryText = `${summaryTextValue} (${billableCharacters} ${summaryCharacterLabel})`;

    this.#setText('[data-price-per-character-count]', billableCharacters.toString());
    this.#setText('[data-price-per-character-summary-text]', summaryText);
    this.#setText('[data-price-per-character-unit-price]', formattedUnitPrice);
    this.#setText('[data-price-per-character-calculation]', `${billableCharacters} × ${formattedUnitPrice} = ${formattedTotalPrice}`);
    this.#setText('[data-price-per-character-total]', discount.percentage > 0 ? formattedDiscountedTotalPrice : formattedTotalPrice);

    this.#setInput('[data-price-per-character-property-count]', billableCharacters.toString());
    this.#setInput('[data-price-per-character-configuration-id]', this.#configurationId(textInput.value, selectedOptionEntries));
    this.#renderSummaryOptions(selectedOptionEntries);
    this.#syncOptionProperties(selectedOptionEntries);

    this.#setText('[data-price-per-character-summary-color-label]', this.#getColorLabel() || 'Farbe');
    this.#setText('[data-price-per-character-summary-color]', this.#getColorValue() || '-');
    const surfaceValue = this.#getSurfaceValue();
    const surfaceFieldset = this.#sectionRoot?.querySelector('[data-price-per-character-surface]');
    this.#sectionRoot?.querySelectorAll('[data-price-per-character-summary-surface-row]').forEach((row) => {
      row.toggleAttribute('hidden', !surfaceValue);
    });
    this.#setText('[data-price-per-character-summary-surface-label]', surfaceFieldset?.dataset.surfaceLabel || 'Oberfläche');
    this.#setText('[data-price-per-character-summary-surface]', surfaceValue || '—');
    this.#updateSurfacePreview();
    this.#updateDiscountPreview(billableCharacters, discount, formattedDiscountedTotalPrice);
    this.#updateUnitPriceDisplay(unitPrice, discount);

    this.#updateAddToCartButton(billableCharacters, discount.percentage > 0 ? formattedDiscountedTotalPrice : formattedTotalPrice);
  }

  #getDiscount(billableCharacters, unitPrice, totalPrice) {
    const tier = discountTiers.find(({ minQuantity }) => billableCharacters >= minQuantity);
    const percentage = tier?.percentage || 0;

    if (percentage <= 0) {
      return { tier, percentage, amount: 0, discountedTotalPrice: totalPrice };
    }

    const discountedUnitPrice = Math.round((unitPrice * (100 - percentage)) / 100);
    const discountedTotalPrice = discountedUnitPrice * billableCharacters;
    const amount = totalPrice - discountedTotalPrice;

    return { tier, percentage, amount, discountedUnitPrice, discountedTotalPrice };
  }

  #getNextDiscountTier(billableCharacters) {
    return [...discountTiers].reverse().find(({ minQuantity }) => billableCharacters < minQuantity);
  }

  #updateDiscountPreview(billableCharacters, discount, formattedDiscountedTotalPrice) {
    const hasCharacters = billableCharacters > 0;
    const nextTier = hasCharacters ? this.#getNextDiscountTier(billableCharacters) : undefined;
    const hasDiscount = discount.percentage > 0;
    const hasHighestTier = hasDiscount && !nextTier;

    this.#setHidden('[data-price-per-character-discount-hint]', !hasCharacters);
    this.#setHidden('[data-price-per-character-discount-row]', !hasDiscount);
    this.#setHidden('[data-price-per-character-discount-note]', !hasDiscount);

    if (!hasCharacters) {
      this.#setDiscountHint('', '');
      this.#setText('[data-price-per-character-discount-label]', 'Mengenrabatt');
      this.#setText('[data-price-per-character-discount-amount]', '-');
      this.#setText('[data-price-per-character-total-label]', 'Gesamtpreis');
      return;
    }

    if (hasHighestTier) {
      this.#setDiscountHint('Höchste Rabattstufe erreicht.', '');
    } else if (hasDiscount) {
      if (nextTier) {
        const missingCharacters = nextTier.minQuantity - billableCharacters;
        this.#setDiscountHint(
          `${discount.percentage} % Mengenrabatt erreicht.`,
          `Noch ${missingCharacters} Zeichen bis ${nextTier.percentage} % Rabatt.`
        );
      }
    } else if (nextTier) {
      const missingCharacters = nextTier.minQuantity - billableCharacters;
      this.#setDiscountHint(`Noch ${missingCharacters} Zeichen bis ${nextTier.percentage} % Mengenrabatt.`, '');
    }

    if (hasDiscount) {
      this.#setText('[data-price-per-character-discount-label]', `${discount.percentage} % Rabatt`);
      this.#setText('[data-price-per-character-discount-amount]', `−${this.#formatPrice(discount.amount)}`);
      this.#setText('[data-price-per-character-total-label]', 'Gesamtpreis');
      this.#setText('[data-price-per-character-total]', formattedDiscountedTotalPrice);
    } else {
      this.#setText('[data-price-per-character-discount-label]', 'Mengenrabatt');
      this.#setText('[data-price-per-character-discount-amount]', '-');
      this.#setText('[data-price-per-character-total-label]', 'Gesamtpreis');
    }
  }

  #setDiscountHint(mainText, subText) {
    this.#setText('[data-price-per-character-discount-hint-main]', mainText);
    this.#setText('[data-price-per-character-discount-hint-sub]', subText);
  }

  #getUnitPriceDisplay() {
    const priceLabel = Array.from(this.#sectionRoot?.querySelectorAll('.text-block') || []).find((element) =>
      element.textContent?.includes('Preis pro Buchstabe bei aktueller Auswahl')
    );
    const labeledPrice = priceLabel?.parentElement?.querySelector('product-price .price');

    if (labeledPrice instanceof HTMLElement) {
      return labeledPrice;
    }

    return /** @type {HTMLElement | null} */ (
      this.#sectionRoot?.querySelector(
        'product-price[data-block-id="price_3g3QcB"] .price__regular .price, product-price[data-block-id="price_3g3QcB"] .price-item--sale.price, product-price[data-block-id="price_3g3QcB"] .price'
      )
    );
  }

  #formatUnitPriceDisplay(cents) {
    const formattedPrice = this.#formatPrice(cents);

    if (this.#unitPriceDisplayUsesLeadingCurrency) {
      return `€${formattedPrice.replace(/\s?€$/, '')}`;
    }

    return formattedPrice;
  }

  #updateUnitPriceDisplay(unitPrice, discount) {
    const display = this.#getUnitPriceDisplay();
    if (!display) return;

    if (this.#unitPriceDisplayUsesLeadingCurrency === undefined) {
      this.#unitPriceDisplayUsesLeadingCurrency = display.textContent?.trim().startsWith('€') || false;
    }

    const regularPrice = this.#formatUnitPriceDisplay(unitPrice);

    if (discount.percentage <= 0 || !discount.discountedUnitPrice) {
      display.textContent = regularPrice;
      return;
    }

    const salePrice = this.#formatUnitPriceDisplay(discount.discountedUnitPrice);
    const comparePrice = document.createElement('s');
    const discountedPrice = document.createElement('span');

    comparePrice.textContent = regularPrice;
    comparePrice.style.opacity = '0.72';
    comparePrice.style.marginRight = '0.5rem';
    discountedPrice.textContent = salePrice;
    discountedPrice.style.fontWeight = '700';

    display.replaceChildren(comparePrice, discountedPrice);
  }

  #configurationId(text, selectedOptionEntries) {
    return JSON.stringify({
      text,
      options: selectedOptionEntries,
      color: this.#getColorValue() || '',
      surface: this.#getSurfaceValue() || '',
    });
  }

  #renderSummaryOptions(selectedOptionEntries) {
    const container = this.#sectionRoot?.querySelector('[data-price-per-character-summary-options]');
    if (!container) return;

    container.replaceChildren(
      ...selectedOptionEntries.map(([optionName, optionValue]) => {
        const row = document.createElement('div');
        const label = document.createElement('dt');
        const value = document.createElement('dd');

        label.textContent = optionName;
        value.textContent = optionValue || '-';
        row.append(label, value);

        return row;
      })
    );
  }

  #syncOptionProperties(selectedOptionEntries) {
    const container = this.#pricePerCharacterContainer;
    const productForm = this.#productForm;
    if (!container || !productForm?.id) return;

    container.querySelectorAll('[data-price-per-character-dynamic-option-property]').forEach((input) => input.remove());

    selectedOptionEntries.forEach(([optionName, optionValue]) => {
      if (!optionName || !optionValue) return;

      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = `properties[${optionName}]`;
      input.value = optionValue;
      input.setAttribute('form', productForm.id);
      input.setAttribute('data-price-per-character-dynamic-option-property', '');
      container.append(input);
    });
  }

  #getSurfaceValue() {
    const input = this.#sectionRoot?.querySelector('[data-price-per-character-surface-input]:checked');
    return input instanceof HTMLInputElement ? input.value : '';
  }

  #getColorValue() {
    const checkedInput = this.#sectionRoot?.querySelector('[data-price-per-character-color-input]:checked');
    if (checkedInput instanceof HTMLInputElement) return checkedInput.value;

    const select = this.#sectionRoot?.querySelector('[data-price-per-character-color-input]');
    if (select instanceof HTMLSelectElement) return select.value;

    return '';
  }

  #getColorLabel() {
    const input = this.#sectionRoot?.querySelector('[data-price-per-character-color-input]');
    return input?.getAttribute('data-price-per-character-color-label') || 'Farbe';
  }

  #updateSurfacePreview() {
    const selectedSurface = this.#getSurfaceValue();
    const previewImages = this.#sectionRoot?.querySelectorAll('[data-price-per-character-surface-image]');
    if (!previewImages?.length) return;

    let hasActiveImage = false;

    previewImages.forEach((image) => {
      const isActive = image.getAttribute('data-price-per-character-surface-image') === selectedSurface;
      image.classList.toggle('is-active', isActive);
      image.toggleAttribute('hidden', !isActive);
      if (isActive) hasActiveImage = true;
    });

    if (!hasActiveImage) {
      const firstImage = previewImages[0];
      firstImage?.classList.add('is-active');
      firstImage?.removeAttribute('hidden');
    }
  }

  #updateAddToCartButton(billableCharacters, formattedTotalPrice) {
    const productForm = this.closest('.shopify-section')?.querySelector('product-form-component[data-price-per-character]');
    const button = productForm?.querySelector('[ref="addToCartButton"]');
    const buttonText = button?.querySelector('.add-to-cart-text__content span span');

    if (!buttonText || !(button instanceof HTMLButtonElement)) return;

    if (!button.dataset.defaultText) {
      button.dataset.defaultText = buttonText.textContent?.trim() || 'In den Warenkorb legen';
    }

    if (billableCharacters > 0) {
      button.disabled = false;
      buttonText.textContent = `Für ${formattedTotalPrice} in den Warenkorb`;
      this.refs.textInput?.setCustomValidity?.('');
    } else {
      button.disabled = true;
      buttonText.textContent = button.dataset.defaultText || 'In den Warenkorb legen';
    }
  }

  /** @param {ProductSelectEvent} event */
  #onProductSelect = async (event) => {
    try {
      const { detail } = await event.promise;
      const price = detail?.resource?.price;

      if (typeof price === 'number') {
        this.#variantPrice = price;
      }

      this.#updatePricePerCharacter();
    } catch (error) {
      if (error?.name !== 'AbortError') console.warn('[price-per-character] Variant update failed:', error);
    }
  };
}

customElements.define('product-custom-property-component', ProductCustomProperty);
